import { useState, useEffect } from 'react';
import { TableRow, TableCell, TextField, Button, Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import StandardTable from '../ui/StandardTable';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);
const round2 = (v) => Math.round(v * 100) / 100;

const TYPE_ICONS = {
  1: <TrendingUpIcon sx={{ fontSize: 18, color: 'success.main' }} />,
  2: <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: 'error.main' }} />,
  3: <StorefrontOutlinedIcon sx={{ fontSize: 18, color: 'warning.main' }} />,
  4: <HandshakeOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />,
};

const TYPE_LABEL = { 1: 'Adicional', 2: 'Desconto', 3: 'Custo', 4: 'Comissão' };

const TYPE_SIGN_COLOR = {
  1: 'success.main',
  2: 'error.main',
  3: 'error.main',
  4: 'error.main',
};

const TYPE_PREFIX = { 1: '+', 2: '-', 3: '-', 4: '-' };

const compactCell = { py: 0.25, px: 1, border: 0 };

// Subcomponente para editar grossValue com estados de edição/visualização
function GrossValueField({ value, onChange }) {
  const [isEditing, setIsEditing] = useState(value === null);
  const [inputValue, setInputValue] = useState(value?.toString() ?? '');

  useEffect(() => {
    if (value != null) {
      setIsEditing(false);
      setInputValue(value.toString());
    }
  }, [value]);

  const handleCommit = () => {
    const num = inputValue ? parseFloat(inputValue) : null;
    if (num !== null && num > 0) {
      onChange(num);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleCommit();
    if (e.key === 'Escape') {
      setInputValue(value?.toString() ?? '');
      setIsEditing(false);
    }
  };

  if (!isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
        <Typography variant="body2" fontWeight={600}>{fmt(value)}</Typography>
        <IconButton
          size="small"
          onClick={() => {
            setIsEditing(true);
            setInputValue(value?.toString() ?? '');
          }}
          sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <TextField
      type="number"
      size="small"
      autoFocus
      placeholder="Informe o valor"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onBlur={handleCommit}
      onKeyDown={handleKeyDown}
      inputProps={{ step: '0.01', min: '0' }}
      sx={{ width: 150 }}
    />
  );
}

function computeRunning(items, baseGross) {
  const withIdx = items.map((item, originalIdx) => ({ item, originalIdx, subtotal: null }));
  withIdx.sort((a, b) =>
    a.item.type !== b.item.type
      ? a.item.type - b.item.type
      : (a.item.displayOrder ?? 0) - (b.item.displayOrder ?? 0)
  );

  const adicionais = withIdx.filter((e) => e.item.type === 1);
  const descontos = withIdx.filter((e) => e.item.type === 2);
  const custos = withIdx.filter((e) => e.item.type === 3);
  const comissoes = withIdx.filter((e) => e.item.type === 4);

  // Gross running (percent applies to running gross)
  let runningGross = baseGross ?? 0;
  for (const e of adicionais) {
    const delta = e.item.valueType === 2 ? runningGross * (e.item.value / 100) : e.item.value;
    runningGross = round2(runningGross + delta);
    e.subtotal = runningGross;
  }
  const finalGross = runningGross;

  // Client running (desconto percent applies to finalGross)
  let runningClient = finalGross;
  for (const e of descontos) {
    const delta = e.item.valueType === 2 ? finalGross * (e.item.value / 100) : e.item.value;
    runningClient = round2(runningClient - delta);
    e.subtotal = runningClient;
  }
  const finalClient = runningClient;

  // Total commission (percent applies to finalClient)
  let totalCommission = 0;
  for (const e of comissoes) {
    const delta = e.item.valueType === 2 ? finalClient * (e.item.value / 100) : e.item.value;
    totalCommission = round2(totalCommission + delta);
  }

  // Custo subtotals: running net before commissions
  let runningCustos = 0;
  for (const e of custos) {
    const delta = e.item.valueType === 2 ? finalClient * (e.item.value / 100) : e.item.value;
    runningCustos = round2(runningCustos + delta);
    e.subtotal = round2(finalClient - runningCustos);
  }
  const totalCustos = runningCustos;

  // Comissão subtotals: net = client - totalCustos - comissoes_so_far
  let runningComissoes = 0;
  for (const e of comissoes) {
    const delta = e.item.valueType === 2 ? finalClient * (e.item.value / 100) : e.item.value;
    runningComissoes = round2(runningComissoes + delta);
    e.subtotal = round2(finalClient - totalCustos - runningComissoes);
  }

  const finalNet = round2(finalClient - totalCustos - totalCommission);

  return { adicionais, descontos, custos, comissoes, finalGross, finalClient, totalCommission, finalNet };
}

function SortableItemRow({ id, item, subtotal, originalIdx, onDelete, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  };

  const showVal = item.valueType === 2 ? `${item.value}%` : fmt(item.value);

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes} hover sx={{ '& td': compactCell }}>
      <TableCell sx={{ width: 52, whiteSpace: 'nowrap' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <Box
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'inline-flex',
              color: 'text.disabled',
              '&:hover': { color: 'text.secondary' },
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 16 }} />
          </Box>
          {TYPE_ICONS[item.type]}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{item.description}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {TYPE_LABEL[item.type]}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color={TYPE_SIGN_COLOR[item.type]} fontWeight={600}>
          {TYPE_PREFIX[item.type]}{showVal}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary">
          {subtotal != null ? fmt(subtotal) : '—'}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ width: 64, whiteSpace: 'nowrap' }}>
        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onEdit?.(originalIdx, item)} title="Editar">
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onDelete(originalIdx)} title="Remover">
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export default function FinanceItemsTable({
  grossValue, onGrossChange, items, onItemsChange, onDeleteItem, onEditItem, onAddClick, serviceName
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const running = grossValue != null ? computeRunning(items, grossValue) : null;

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const activeOrigIdx = Number(active.id);
    const overOrigIdx = Number(over.id);
    const activeItem = items[activeOrigIdx];
    const overItem = items[overOrigIdx];

    if (!activeItem || !overItem || activeItem.type !== overItem.type) return;

    // Group entries sorted by displayOrder
    const groupEntries = items
      .map((item, idx) => ({ item, idx }))
      .filter((e) => e.item.type === activeItem.type)
      .sort((a, b) => (a.item.displayOrder ?? 0) - (b.item.displayOrder ?? 0));

    const fromPos = groupEntries.findIndex((e) => e.idx === activeOrigIdx);
    const toPos = groupEntries.findIndex((e) => e.idx === overOrigIdx);

    if (fromPos < 0 || toPos < 0 || fromPos === toPos) return;

    const reordered = arrayMove(groupEntries, fromPos, toPos);

    const newItems = [...items];
    reordered.forEach(({ idx }, order) => {
      newItems[idx] = { ...newItems[idx], displayOrder: order };
    });

    onItemsChange(newItems);
  };

  const columns = [
    { id: 'icon', label: '', width: 52 },
    { id: 'description', label: 'Descrição' },
    { id: 'value', label: 'Valor', align: 'right' },
    { id: 'subtotal', label: 'Subtotal', align: 'right' },
    { id: 'actions', label: '', align: 'right', width: 64 },
  ];

  const renderSortableGroup = (group) => {
    const ids = group.map((e) => String(e.originalIdx));
    return (
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {group.map((e) => (
          <SortableItemRow
            key={e.originalIdx}
            id={String(e.originalIdx)}
            item={e.item}
            subtotal={e.subtotal}
            originalIdx={e.originalIdx}
            onDelete={onDeleteItem}
            onEdit={onEditItem}
          />
        ))}
      </SortableContext>
    );
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <StandardTable columns={columns} isEmpty={false}>
          {/* Linha base do serviço — sempre visível, não é arrastável */}
          <TableRow sx={{ '& td': compactCell }}>
            <TableCell />
            <TableCell>
              <Typography variant="body2" fontWeight={600}>
                {serviceName || 'Serviço'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Base
              </Typography>
            </TableCell>
            <TableCell align="right">
              <GrossValueField value={grossValue} onChange={onGrossChange} />
            </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>

          {grossValue != null && running && (
            <>
              {renderSortableGroup(running.adicionais)}
              {renderSortableGroup(running.descontos)}

              {/* Linha sumário: Total do Cliente */}
              <TableRow sx={{ backgroundColor: 'action.selected', '& td': compactCell }}>
                <TableCell />
                <TableCell colSpan={2}>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem' }}>
                    Total do Cliente
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>{fmt(running.finalClient)}</Typography>
                </TableCell>
                <TableCell />
              </TableRow>

              {renderSortableGroup(running.custos)}
              {renderSortableGroup(running.comissoes)}

              {/* Linha resumo final com os 4 totais */}
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell colSpan={5} sx={{ py: 0.75, px: 1 }}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', letterSpacing: 0.5 }}>BRUTO</Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.1 }}>{fmt(running.finalGross)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', letterSpacing: 0.5 }}>CLIENTE</Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.1 }}>{fmt(running.finalClient)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', letterSpacing: 0.5 }}>COMISSÕES</Typography>
                      <Typography variant="body1" fontWeight={600} color="warning.main" sx={{ lineHeight: 1.1 }}>{fmt(running.totalCommission)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', letterSpacing: 0.5 }}>LÍQUIDO</Typography>
                      <Typography variant="body1" fontWeight={700} color="primary.main" sx={{ lineHeight: 1.1 }}>{fmt(running.finalNet)}</Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            </>
          )}
        </StandardTable>
      </DndContext>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddClick}
          disabled={grossValue == null}
        >
          Adicionar item
        </Button>
      </Box>
    </>
  );
}
